import React, { useEffect, useMemo, useState, useRef } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Bike, Newspaper, MapPin, Inbox, Settings as SettingsIcon, Plus, Pencil, Trash2, Download, LogOut, X, Check, FileText, ShieldCheck, RefreshCw, Mail, Eye, Search, Tags, Phone, MessageCircle, ExternalLink, Clock, Filter, FileDown, Calendar, Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import api, { formatPrice, uploadFile, resolveAsset } from "@/lib/api";
import { toast } from "sonner";
import AdminLogin from "@/pages/AdminLogin";
import { purposeLabel } from "@/lib/visitor";
import { datePresetRange, exportLeadsPdf, leadInDateRange } from "@/lib/leadsPdf";
import { selectOption } from "@/lib/selectOption";

const ADMIN_MOBILE_TITLES = [
    ["/auth/secure-gate/bikes", "Manage Bikes"],
    ["/auth/secure-gate/news", "Manage News"],
    ["/auth/secure-gate/dealers", "Manage Branches"],
    ["/auth/secure-gate/brands", "Brand Logos"],
    ["/auth/secure-gate/messages", "Messages"],
    ["/auth/secure-gate/cms", "Homepage CMS"],
    ["/auth/secure-gate/pages", "Page Content"],
    ["/auth/secure-gate/leads", "Leads"],
    ["/auth/secure-gate/settings", "Settings"],
    ["/auth/secure-gate", "Dashboard"],
];

const adminMobileTitle = (pathname) => {
    const hit = ADMIN_MOBILE_TITLES.find(([p]) => pathname === p || (p !== "/auth/secure-gate" && pathname.startsWith(p)));
    return hit ? hit[1] : "Admin";
};

/* ===== Sidebar ===== */
const Sidebar = ({ open, onClose }) => {
    const { logout } = useAuth();
    const nav = useNavigate();
    const cls = ({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium transition-colors
            ${isActive ? "bg-[#ED1C24] text-white" : "text-[#0f0f12] hover:bg-[#fff5f5] hover:text-[#ED1C24]"}`;

    return (
        <>
            <button
                type="button"
                aria-label="Close admin menu"
                onClick={onClose}
                className={`fixed inset-0 z-40 bg-black/40 lg:hidden ${open ? "block" : "hidden"}`}
                data-testid="admin-sidebar-backdrop"
            />
            <aside
                className={`fixed lg:static inset-y-0 left-0 z-50 w-64 max-w-[85vw] lg:max-w-none h-screen lg:h-full lg:shrink-0 bg-white border-r border-[#e5e7eb] p-5 flex flex-col overflow-hidden transform transition-transform duration-200 ease-out ${open ? "translate-x-0" : "-translate-x-full pointer-events-none lg:pointer-events-auto"} lg:translate-x-0`}
                data-testid="admin-sidebar"
            >
                <div className="flex items-center justify-between gap-2 mb-8">
                    <Link to="/" className="flex items-center gap-2 min-w-0" onClick={onClose}>
                        <div className="w-9 h-9 rounded-lg bg-[#ED1C24] flex items-center justify-center text-white font-display font-black shrink-0">S</div>
                        <div>
                            <p className="font-display font-bold leading-tight">SERVALL</p>
                            <p className="text-[10px] text-[#7a7a84] uppercase tracking-widest">Admin Panel</p>
                        </div>
                    </Link>
                    <button
                        type="button"
                        className="lg:hidden min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg hover:bg-[#fafafa]"
                        onClick={onClose}
                        aria-label="Close menu"
                        data-testid="admin-sidebar-close"
                    >
                        <X size={18} />
                    </button>
                </div>
                <nav className="flex flex-col gap-1 overflow-y-auto flex-1 min-h-0">
                    <NavLink to="/auth/secure-gate" end className={cls} onClick={onClose} data-testid="admin-nav-dashboard"><LayoutDashboard size={16} /> Dashboard</NavLink>
                    <NavLink to="/auth/secure-gate/bikes" className={cls} onClick={onClose} data-testid="admin-nav-bikes"><Bike size={16} /> Manage Bikes</NavLink>
                    <NavLink to="/auth/secure-gate/news" className={cls} onClick={onClose} data-testid="admin-nav-news"><Newspaper size={16} /> Manage News</NavLink>
                    <NavLink to="/auth/secure-gate/dealers" className={cls} onClick={onClose} data-testid="admin-nav-dealers"><MapPin size={16} /> Manage Branches</NavLink>
                    <NavLink to="/auth/secure-gate/brands" className={cls} onClick={onClose} data-testid="admin-nav-brands"><Tags size={16} /> Brand Logos</NavLink>
                    <NavLink to="/auth/secure-gate/messages" className={cls} onClick={onClose} data-testid="admin-nav-messages"><Mail size={16} /> Contact Messages</NavLink>
                    <NavLink to="/auth/secure-gate/cms" className={cls} onClick={onClose} data-testid="admin-nav-cms"><FileText size={16} /> Homepage CMS</NavLink>
                    <NavLink to="/auth/secure-gate/pages" className={cls} onClick={onClose} data-testid="admin-nav-pages"><FileText size={16} /> Page Content</NavLink>
                    <NavLink to="/auth/secure-gate/leads" className={cls} onClick={onClose} data-testid="admin-nav-leads"><Inbox size={16} /> Leads</NavLink>
                    <NavLink to="/auth/secure-gate/settings" className={cls} onClick={onClose} data-testid="admin-nav-settings"><SettingsIcon size={16} /> Settings</NavLink>
                </nav>
                <div className="mt-auto pt-5 border-t border-[#e5e7eb]">
                    <button onClick={() => { logout(); nav("/"); }} className="flex items-center gap-2 text-sm text-[#ED1C24] font-semibold min-h-[44px]" data-testid="admin-logout">
                        <LogOut size={14} /> Logout
                    </button>
                </div>
            </aside>
        </>
    );
};

/* ===== Dashboard ===== */
const Dashboard = () => {
    const nav = useNavigate();
    const { settings } = useSettings();
    const [stats, setStats] = useState(null);

    useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);

    if (!stats) return <p className="text-[#7a7a84]">Loading…</p>;

    const cards = [
        { label: "Bikes on sale", value: stats.total_bikes, color: "from-[#ED1C24] to-[#C0151B]", to: "/auth/secure-gate/bikes" },
        {
            label: "New leads",
            value: stats.new_leads ?? 0,
            sub: stats.total_leads ? `${stats.total_leads} total` : null,
            color: "from-[#EC4899] to-[#BE185D]",
            to: "/auth/secure-gate/leads",
            highlight: (stats.new_leads ?? 0) > 0,
        },
        {
            label: "Unread messages",
            value: stats.unread_messages ?? 0,
            color: "from-[#F59E0B] to-[#d18602]",
            to: "/auth/secure-gate/messages",
            highlight: (stats.unread_messages ?? 0) > 0,
        },
        { label: "Branches", value: stats.total_dealers, color: "from-[#3B82F6] to-[#2563EB]", to: "/auth/secure-gate/dealers" },
        { label: "Brands", value: stats.total_brands ?? 0, color: "from-[#8B5CF6] to-[#6D28D9]", to: "/auth/secure-gate/brands" },
        { label: "News articles", value: stats.total_news ?? 0, color: "from-[#14B8A6] to-[#0D9488]", to: "/auth/secure-gate/news" },
        { label: "Featured", value: stats.featured_bikes, color: "from-[#0f0f12] to-[#2a2a2e]", to: "/auth/secure-gate/bikes" },
        { label: "Coming soon", value: stats.upcoming_bikes, color: "from-[#10B981] to-[#0e9d6e]", to: "/auth/secure-gate/bikes" },
    ];

    const trackingChecks = [
        { ok: settings?.google_analytics_id, label: "GA4 ID set", to: "/auth/secure-gate/settings" },
        { ok: settings?.google_ads_conversion_label || settings?.google_ads_id, label: "Google Ads tracking", to: "/auth/secure-gate/settings" },
        { ok: settings?.meta_pixel_id, label: "Meta Pixel ID set", to: "/auth/secure-gate/settings" },
        { ok: settings?.seo_og_image, label: "Social preview image (og:image)", to: "/auth/secure-gate/settings" },
        { ok: settings?.google_site_verification, label: "Search Console verification", to: "/auth/secure-gate/settings" },
    ];
    const seoReady = trackingChecks.filter(c => c.ok).length;

    return (
        <div data-testid="admin-dashboard">
            <h1 className="font-display font-bold text-2xl sm:text-3xl mb-2">Dashboard</h1>
            <p className="text-sm text-[#7a7a84] mb-6">Sales pipeline at a glance — click a card to manage that section.</p>

            {(stats.new_leads > 0 || stats.unread_messages > 0) && (
                <div className="flex flex-wrap gap-3 mb-6">
                    {stats.new_leads > 0 && (
                        <button type="button" onClick={() => nav("/auth/secure-gate/leads")} className="btn btn-primary btn-sm">
                            {stats.new_leads} new lead{stats.new_leads === 1 ? "" : "s"} — review
                        </button>
                    )}
                    {stats.unread_messages > 0 && (
                        <button type="button" onClick={() => nav("/auth/secure-gate/messages")} className="btn btn-white btn-sm border border-[#F59E0B] text-[#B45309]">
                            {stats.unread_messages} unread message{stats.unread_messages === 1 ? "" : "s"}
                        </button>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 mb-8">
                {cards.map(c => (
                    <button
                        key={c.label}
                        type="button"
                        onClick={() => nav(c.to)}
                        className={`rounded-2xl p-3 sm:p-5 text-white bg-gradient-to-br ${c.color} text-left transition-transform hover:scale-[1.02] ${c.highlight ? "ring-2 ring-white ring-offset-2 ring-offset-[#fafafa]" : ""}`}
                    >
                        <p className="text-[10px] sm:text-xs uppercase font-display font-bold tracking-wider opacity-80 leading-tight">{c.label}</p>
                        <p className="font-display font-black text-2xl sm:text-4xl mt-2" data-testid={`stat-${c.label.replace(/\s/g, '-').toLowerCase()}`}>{c.value}</p>
                        {c.sub ? <p className="text-[11px] mt-1 opacity-80">{c.sub}</p> : null}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="bike-card p-6">
                    <h2 className="font-display font-bold text-lg mb-1">SEO &amp; tracking setup</h2>
                    <p className="text-xs text-[#7a7a84] mb-4">{seoReady}/{trackingChecks.length} configured — finish before launch.</p>
                    <ul className="space-y-2">
                        {trackingChecks.map((c) => (
                            <li key={c.label}>
                                <button type="button" onClick={() => nav(c.to)} className="flex items-center gap-2 text-sm w-full text-left hover:text-[#ED1C24]">
                                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${c.ok ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-400"}`}>
                                        {c.ok ? "✓" : "·"}
                                    </span>
                                    {c.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                    <button type="button" onClick={() => nav("/auth/secure-gate/pages")} className="mt-4 text-sm font-semibold text-[#ED1C24] hover:underline">
                        Edit on-page SEO titles →
                    </button>
                </div>

                <div className="bike-card p-6">
                    <h2 className="font-display font-bold text-lg mb-4">Quick links</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {[
                            ["Homepage hero & carousel", "/auth/secure-gate/cms"],
                            ["Page copy & SEO", "/auth/secure-gate/pages"],
                            ["Tracking & global SEO", "/auth/secure-gate/settings"],
                            ["Export leads CSV", "/auth/secure-gate/leads"],
                        ].map(([label, to]) => (
                            <button key={to} type="button" onClick={() => nav(to)} className="text-left text-sm px-3 py-2 rounded-lg border border-zinc-200 hover:border-[#ED1C24]/30 hover:bg-[#fff5f5]">
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ===== Reusable Modal ===== */
const Modal = ({ open, onClose, title, children, wide }) => {
    if (!open) return null;
    return (
        <div onClick={onClose} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div onClick={(e) => e.stopPropagation()} className={`bg-white rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto`}>
                <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[#e5e7eb] sticky top-0 bg-white">
                    <h3 className="font-display font-bold text-lg pr-2">{title}</h3>
                    <button onClick={onClose} className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#fafafa] rounded" aria-label="Close"><X size={18} /></button>
                </div>
                <div className="p-4 sm:p-5">{children}</div>
            </div>
        </div>
    );
};

const TextField = ({ label, value, onChange, type = "text", ...rest }) => (
    <div>
        <label className="label">{label}</label>
        <input type={type} className="input" value={value ?? ""} onChange={(e) => onChange(type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value)} {...rest} />
    </div>
);

const Toggle = ({ label, checked, onChange }) => (
    <label className="flex items-center justify-between p-3 rounded-lg border border-[#e5e7eb] cursor-pointer">
        <span className="text-sm font-medium">{label}</span>
        <button type="button" onClick={() => onChange(!checked)} className={`w-10 h-6 rounded-full transition-colors ${checked ? "bg-[#ED1C24]" : "bg-[#e5e7eb]"}`}>
            <span className={`block w-5 h-5 rounded-full bg-white transition-transform translate-y-0.5 ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`} />
        </button>
    </label>
);

/* ===== Manage Bikes ===== */
const SUBS_BY_CAT = {
    "Scooty & EV": ["110cc Scooty", "125cc Sport Scooty", "Electric Scooter"],
    "Motorcycle": ["125cc+", "150cc+", "200cc+", "350cc & Above"],
};
const emptyBike = {
    name: "", brand: "", category: "Motorcycle", subcategory: "150cc+",
    fuel_type: "Petrol", engine_cc: 0, price_min: 0, price_max: 0,
    image: "", images: [], colors: [], power_bhp: 0, torque_nm: 0,
    top_speed: 0, mileage: 0, fuel_tank: 0, weight: 0,
    abs: false, bluetooth: false, battery_kwh: 0, range_km: 0,
    is_featured: false, is_upcoming: false, rating: 4.0, popularity: 50,
    description: "", pros: [], cons: [], youtube_url: "",
};

/* ===== Image Uploader Components (used in Manage Bikes) ===== */
const ImageSizeHint = ({ children }) => (
    <p className="text-[11px] text-[#7a7a84] leading-relaxed mt-1">{children}</p>
);

const SingleImageUploader = ({ value, onChange, testIdPrefix = "img", sizeHint }) => {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);

    const onFile = async (f) => {
        if (!f) return;
        setBusy(true);
        try {
            const { url } = await uploadFile(f);
            onChange(url);
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
    };

    return (
        <div className="space-y-2">
            {value ? (
                <div className="relative group inline-block border border-zinc-200 rounded-lg overflow-hidden bg-[#F4F6F8]">
                    <img src={resolveAsset(value)} alt="" className="w-40 h-32 object-contain" />
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="absolute top-1 right-1 bg-[#E31837] text-white w-7 h-7 rounded-full flex items-center justify-center opacity-90 hover:opacity-100"
                        data-testid={`${testIdPrefix}-remove`}
                        title="Remove"
                    >
                        <Trash2 size={12} />
                    </button>
                </div>
            ) : (
                <div
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2", "ring-[#E31837]"); }}
                    onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-[#E31837]")}
                    onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("ring-2", "ring-[#E31837]"); onFile(e.dataTransfer.files?.[0]); }}
                    onClick={() => ref.current?.click()}
                    className="border-2 border-dashed border-zinc-300 rounded-lg p-5 text-center cursor-pointer hover:bg-zinc-50 transition"
                    data-testid={`${testIdPrefix}-drop`}
                >
                    <p className="text-sm font-bold text-[#0A2540]">{busy ? "Uploading…" : "📷 Click or drop image"}</p>
                    <p className="text-[11px] text-zinc-500 mt-1">JPG / PNG / WEBP · max 8 MB</p>
                    {sizeHint ? <p className="text-[11px] text-[#0A2540]/80 mt-1 font-medium">{sizeHint}</p> : null}
                </div>
            )}
            {value && sizeHint ? <ImageSizeHint>{sizeHint}</ImageSizeHint> : null}
            <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/jpg" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid={`${testIdPrefix}-input`} />
        </div>
    );
};

const MultiImageUploader = ({ value = [], onChange, testIdPrefix = "imgs", sizeHint }) => {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);

    const addFiles = async (files) => {
        if (!files || files.length === 0) return;
        setBusy(true);
        const next = [...value];
        try {
            for (const f of Array.from(files)) {
                if (next.length >= 12) { toast.error("Max 12 images"); break; }
                const { url } = await uploadFile(f);
                next.push(url);
            }
            onChange(next);
            toast.success(`${files.length} image(s) uploaded`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
    };

    const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

    return (
        <div className="space-y-3">
            <div
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2", "ring-[#E31837]"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-[#E31837]")}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("ring-2", "ring-[#E31837]"); addFiles(e.dataTransfer.files); }}
                onClick={() => ref.current?.click()}
                className="border-2 border-dashed border-zinc-300 rounded-lg p-5 text-center cursor-pointer hover:bg-zinc-50 transition"
                data-testid={`${testIdPrefix}-drop`}
            >
                <p className="text-sm font-bold text-[#0A2540]">{busy ? "Uploading…" : "📷 Drop multiple images or click to pick"}</p>
                <p className="text-[11px] text-zinc-500 mt-1">JPG / PNG / WEBP · max 8 MB each · {value.length}/12 added</p>
                {sizeHint ? <p className="text-[11px] text-[#0A2540]/80 mt-1 font-medium">{sizeHint}</p> : null}
            </div>
            <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/jpg" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid={`${testIdPrefix}-input`} />

            {value.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {value.map((url, i) => (
                        <div key={`${url}-${i}`} className="relative group aspect-square bg-[#F4F6F8] border border-zinc-200 rounded-md overflow-hidden" data-testid={`${testIdPrefix}-${i}`}>
                            <img src={resolveAsset(url)} alt="" className="w-full h-full object-contain p-1" />
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                className="absolute top-1 right-1 bg-[#E31837] text-white w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                                data-testid={`${testIdPrefix}-remove-${i}`}
                            >
                                <Trash2 size={10} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ManageBikes = () => {
    const [bikes, setBikes] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState(emptyBike);
    const [search, setSearch] = useState("");

    const load = () => api.get("/bikes?limit=500").then(r => setBikes(r.data.items || []));
    useEffect(() => { load(); }, []);

    const openCreate = () => { setForm(emptyBike); setEdit(null); setOpen(true); };
    const openEdit = (b) => {
        setForm({ ...emptyBike, ...b });
        setEdit(b);
        setOpen(true);
    };

    const save = async () => {
        try {
            const body = {
                ...form,
                images: typeof form.images === "string" ? form.images.split(",").map(s => s.trim()).filter(Boolean) : form.images,
                colors: typeof form.colors === "string" ? form.colors.split(",").map(s => s.trim()).filter(Boolean) : form.colors,
                pros: typeof form.pros === "string" ? form.pros.split("\n").map(s => s.trim()).filter(Boolean) : form.pros,
                cons: typeof form.cons === "string" ? form.cons.split("\n").map(s => s.trim()).filter(Boolean) : form.cons,
            };
            if (edit) await api.put(`/admin/bikes/${edit.id}`, body);
            else await api.post("/admin/bikes", body);
            toast.success(edit ? "Bike updated" : "Bike added");
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const remove = async (b) => {
        if (!window.confirm(`Delete ${b.name}?`)) return;
        await api.delete(`/admin/bikes/${b.id}`);
        toast.success("Deleted");
        load();
    };

    const filtered = bikes.filter(b =>
        !search || (b.name + " " + b.brand).toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div data-testid="admin-bikes-page">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <h1 className="font-display font-bold text-2xl sm:text-3xl">Manage Bikes</h1>
                <button onClick={openCreate} className="btn btn-primary btn-sm" data-testid="admin-add-bike"><Plus size={14} /> Add Bike</button>
            </div>
            <input className="input mb-4" placeholder="Search by name or brand…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-bikes-search" />

            <div className="bike-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#fafafa]">
                        <tr className="text-left uppercase text-xs text-[#7a7a84]">
                            <th className="p-3">Bike</th>
                            <th className="p-3">Category</th>
                            <th className="p-3">Price</th>
                            <th className="p-3">Flags</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(b => (
                            <tr key={b.id} className="border-t border-[#e5e7eb]" data-testid={`admin-bike-row-${b.id}`}>
                                <td className="p-3">
                                    <div className="flex items-center gap-3">
                                        <img src={resolveAsset(b.image)} alt={b.name} className="w-12 h-12 rounded-lg object-cover" />
                                        <div>
                                            <p className="font-display font-bold">{b.name}</p>
                                            <p className="text-xs text-[#7a7a84]">{b.brand}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-3">{b.subcategory}</td>
                                <td className="p-3">{formatPrice(b.price_min)}</td>
                                <td className="p-3">
                                    {b.is_featured && <span className="chip chip-red text-[10px] mr-1">Featured</span>}
                                    {b.is_upcoming && <span className="chip text-[10px]">Upcoming</span>}
                                </td>
                                <td className="p-3 text-right">
                                    <button onClick={() => openEdit(b)} className="p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#fff5f5] hover:text-[#ED1C24] rounded" data-testid={`admin-bike-edit-${b.id}`}><Pencil size={14} /></button>
                                    <button onClick={() => remove(b)} className="p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#fff5f5] hover:text-[#ED1C24] rounded" data-testid={`admin-bike-delete-${b.id}`}><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Bike" : "Add Bike"} wide>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                    <TextField label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />

                    <div>
                        <label className="label">Category</label>
                        <select className="input" value={form.category} onChange={(e) => {
                            const cat = e.target.value;
                            setForm({ ...form, category: cat, subcategory: SUBS_BY_CAT[cat][0] });
                        }}>
                            <option>Scooty & EV</option>
                            <option>Motorcycle</option>
                        </select>
                    </div>
                    <div>
                        <label className="label">Subcategory</label>
                        <select className="input" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })}>
                            {(SUBS_BY_CAT[form.category] || []).map((s) => selectOption(s))}
                        </select>
                    </div>

                    <div>
                        <label className="label">Fuel Type</label>
                        <select className="input" value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })}>
                            <option>Petrol</option>
                            <option>Electric</option>
                        </select>
                    </div>
                    <TextField label="Engine CC" type="number" value={form.engine_cc} onChange={(v) => setForm({ ...form, engine_cc: v })} />

                    <TextField label="Price Min (₹)" type="number" value={form.price_min} onChange={(v) => setForm({ ...form, price_min: v })} />
                    <TextField label="Price Max (₹)" type="number" value={form.price_max} onChange={(v) => setForm({ ...form, price_max: v })} />

                    <div className="md:col-span-2">
                        <label className="label">Main Image (single upload)</label>
                        <SingleImageUploader
                            value={form.image}
                            onChange={(url) => setForm({ ...form, image: url })}
                            testIdPrefix="bike-main-image"
                            sizeHint="Recommended: 1200×900 px (4:3). Min 800×600. Keep subject centered."
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">Additional Images (multiple uploads)</label>
                        <MultiImageUploader
                            value={Array.isArray(form.images) ? form.images : (typeof form.images === "string" && form.images ? form.images.split(",").map(s => s.trim()).filter(Boolean) : [])}
                            onChange={(arr) => setForm({ ...form, images: arr })}
                            testIdPrefix="bike-additional"
                            sizeHint="Recommended: 1200×900 px (4:3) each · same crop as main image"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">Colors (comma-separated)</label>
                        <input className="input" value={Array.isArray(form.colors) ? form.colors.join(", ") : form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} />
                    </div>

                    <TextField label="Power (bhp)" type="number" value={form.power_bhp} onChange={(v) => setForm({ ...form, power_bhp: v })} />
                    <TextField label="Torque (Nm)" type="number" value={form.torque_nm} onChange={(v) => setForm({ ...form, torque_nm: v })} />
                    <TextField label="Top Speed (km/h)" type="number" value={form.top_speed} onChange={(v) => setForm({ ...form, top_speed: v })} />
                    <TextField label="Mileage (kmpl)" type="number" value={form.mileage} onChange={(v) => setForm({ ...form, mileage: v })} />
                    <TextField label="Fuel Tank (L)" type="number" value={form.fuel_tank} onChange={(v) => setForm({ ...form, fuel_tank: v })} />
                    <TextField label="Weight (kg)" type="number" value={form.weight} onChange={(v) => setForm({ ...form, weight: v })} />
                    <TextField label="Battery (kWh)" type="number" value={form.battery_kwh} onChange={(v) => setForm({ ...form, battery_kwh: v })} />
                    <TextField label="Range (km)" type="number" value={form.range_km} onChange={(v) => setForm({ ...form, range_km: v })} />
                    <TextField label="Rating (0-5)" type="number" value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
                    <TextField label="Popularity (0-100)" type="number" value={form.popularity} onChange={(v) => setForm({ ...form, popularity: v })} />

                    <div className="md:col-span-2">
                        <TextField label="YouTube URL (per-bike video, shown on detail page)" value={form.youtube_url} onChange={(v) => setForm({ ...form, youtube_url: v })} placeholder="https://www.youtube.com/watch?v=…" />
                    </div>

                    <Toggle label="ABS" checked={form.abs} onChange={(v) => setForm({ ...form, abs: v })} />
                    <Toggle label="Bluetooth" checked={form.bluetooth} onChange={(v) => setForm({ ...form, bluetooth: v })} />
                    <Toggle label="Featured" checked={form.is_featured} onChange={(v) => setForm({ ...form, is_featured: v })} />
                    <Toggle label="Upcoming" checked={form.is_upcoming} onChange={(v) => setForm({ ...form, is_upcoming: v })} />

                    <div className="md:col-span-2">
                        <label className="label">Description</label>
                        <textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    </div>
                    <div>
                        <label className="label">Pros (one per line)</label>
                        <textarea className="input" rows={3} value={Array.isArray(form.pros) ? form.pros.join("\n") : form.pros} onChange={(e) => setForm({ ...form, pros: e.target.value })} />
                    </div>
                    <div>
                        <label className="label">Cons (one per line)</label>
                        <textarea className="input" rows={3} value={Array.isArray(form.cons) ? form.cons.join("\n") : form.cons} onChange={(e) => setForm({ ...form, cons: e.target.value })} />
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={() => setOpen(false)} className="btn btn-white btn-sm">Cancel</button>
                    <button onClick={save} className="btn btn-primary btn-sm" data-testid="admin-save-bike">{edit ? "Save Changes" : "Add Bike"}</button>
                </div>
            </Modal>
        </div>
    );
};

/* ===== Manage News ===== */
const emptyNews = { title: "", slug: "", category: "News", image: "", excerpt: "", content: "", author: "Servall Team" };
const ManageNews = () => {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState(emptyNews);
    const load = () => api.get("/news?limit=200").then(r => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            if (edit) await api.put(`/admin/news/${edit.id}`, form);
            else await api.post("/admin/news", form);
            toast.success(edit ? "Article updated" : "Article added");
            setOpen(false); load();
        } catch (e) { toast.error("Failed"); }
    };

    const remove = async (n) => {
        if (!window.confirm(`Delete "${n.title}"?`)) return;
        await api.delete(`/admin/news/${n.id}`);
        toast.success("Deleted"); load();
    };

    return (
        <div data-testid="admin-news-page">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <h1 className="font-display font-bold text-2xl sm:text-3xl">Manage News</h1>
                <button onClick={() => { setForm(emptyNews); setEdit(null); setOpen(true); }} className="btn btn-primary btn-sm"><Plus size={14} /> Add Article</button>
            </div>
            <div className="bike-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#fafafa]"><tr className="text-left uppercase text-xs text-[#7a7a84]"><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Author</th><th className="p-3"></th></tr></thead>
                    <tbody>
                        {items.map(n => (
                            <tr key={n.id} className="border-t border-[#e5e7eb]">
                                <td className="p-3 font-medium">{n.title}</td>
                                <td className="p-3"><span className="chip chip-red text-[10px]">{n.category}</span></td>
                                <td className="p-3">{n.author}</td>
                                <td className="p-3 text-right">
                                    <button onClick={() => { setForm(n); setEdit(n); setOpen(true); }} className="p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#fff5f5] hover:text-[#ED1C24] rounded"><Pencil size={14} /></button>
                                    <button onClick={() => remove(n)} className="p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#fff5f5] hover:text-[#ED1C24] rounded"><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Article" : "Add Article"} wide>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                    <TextField label="Slug (optional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
                    <div>
                        <label className="label">Category</label>
                        <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                            <option>News</option><option>Reviews</option><option>Tips</option><option>EV</option><option>Launches</option>
                        </select>
                    </div>
                    <TextField label="Author" value={form.author} onChange={(v) => setForm({ ...form, author: v })} />
                    <div className="md:col-span-2">
                        <TextField label="Image URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
                        <ImageSizeHint>Recommended: 1200×675 px (16:9) · JPG/PNG/WEBP · max 8 MB</ImageSizeHint>
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">Excerpt</label>
                        <textarea className="input" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">Content</label>
                        <textarea className="input" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={() => setOpen(false)} className="btn btn-white btn-sm">Cancel</button>
                    <button onClick={save} className="btn btn-primary btn-sm">Save</button>
                </div>
            </Modal>
        </div>
    );
};

/* ===== Manage Dealers ===== */
const emptyDealer = { name: "", brand: "", city: "", address: "", phone: "", email: "", lat: 0, lng: 0, working_hours: "Mon-Sat 10AM - 8PM" };
const ManageDealers = () => {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState(emptyDealer);
    const load = () => api.get("/dealers").then(r => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            if (edit) await api.put(`/admin/dealers/${edit.id}`, form);
            else await api.post("/admin/dealers", form);
            toast.success(edit ? "Branch updated" : "Branch added");
            setOpen(false); load();
        } catch (e) { toast.error("Failed"); }
    };

    const remove = async (d) => {
        if (!window.confirm(`Delete ${d.name}?`)) return;
        await api.delete(`/admin/dealers/${d.id}`);
        toast.success("Deleted"); load();
    };

    return (
        <div data-testid="admin-dealers-page">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <h1 className="font-display font-bold text-2xl sm:text-3xl">Manage Branches</h1>
                <button onClick={() => { setForm(emptyDealer); setEdit(null); setOpen(true); }} className="btn btn-primary btn-sm"><Plus size={14} /> Add Branch</button>
            </div>
            <div className="bike-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#fafafa]"><tr className="text-left uppercase text-xs text-[#7a7a84]"><th className="p-3">Branch</th><th className="p-3">Brand</th><th className="p-3">City</th><th className="p-3">Phone</th><th className="p-3"></th></tr></thead>
                    <tbody>
                        {items.map(d => (
                            <tr key={d.id} className="border-t border-[#e5e7eb]">
                                <td className="p-3 font-medium">{d.name}</td>
                                <td className="p-3"><span className="chip chip-brand text-[10px]">{d.brand}</span></td>
                                <td className="p-3">{d.city}</td>
                                <td className="p-3">{d.phone}</td>
                                <td className="p-3 text-right">
                                    <button onClick={() => { setForm(d); setEdit(d); setOpen(true); }} className="p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#fff5f5] hover:text-[#ED1C24] rounded"><Pencil size={14} /></button>
                                    <button onClick={() => remove(d)} className="p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#fff5f5] hover:text-[#ED1C24] rounded"><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Branch" : "Add Branch"} wide>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Branch Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                    <TextField label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
                    <TextField label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
                    <TextField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                    <div className="md:col-span-2"><TextField label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} /></div>
                    <div className="md:col-span-2"><TextField label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} /></div>
                    <TextField label="Latitude" type="number" value={form.lat} onChange={(v) => setForm({ ...form, lat: v })} />
                    <TextField label="Longitude" type="number" value={form.lng} onChange={(v) => setForm({ ...form, lng: v })} />
                    <div className="md:col-span-2"><TextField label="Working Hours" value={form.working_hours} onChange={(v) => setForm({ ...form, working_hours: v })} /></div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={() => setOpen(false)} className="btn btn-white btn-sm">Cancel</button>
                    <button onClick={save} className="btn btn-primary btn-sm">Save</button>
                </div>
            </Modal>
        </div>
    );
};

/* ===== Manage Brands (homepage logos) ===== */
const emptyBrand = { name: "", logo_url: "", sort_order: 0, is_active: true };
const ManageBrands = () => {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState(emptyBrand);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = () => api.get("/admin/brands").then(r => setItems(r.data?.items || []));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            if (edit) await api.put(`/admin/brands/${edit.id}`, form);
            else await api.post("/admin/brands", form);
            toast.success(edit ? "Brand updated" : "Brand added");
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save brand");
        }
    };

    const remove = async (b) => {
        if (!window.confirm(`Remove ${b.name} from homepage brands?`)) return;
        await api.delete(`/admin/brands/${b.id}`);
        toast.success("Brand removed");
        load();
    };

    const syncFromCatalog = async () => {
        try {
            const { data } = await api.post("/admin/brands/sync-from-catalog");
            toast.success(data.added ? `Added ${data.added} brand(s) from bike catalog` : "All catalog brands are already listed");
            load();
        } catch {
            toast.error("Sync failed");
        }
    };

    const onLogoUpload = async (files) => {
        const f = files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const { url } = await uploadFile(f);
            setForm(prev => ({ ...prev, logo_url: url }));
            toast.success("Logo uploaded");
        } catch {
            toast.error("Logo upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div data-testid="admin-brands-page">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl">Brand Logos</h1>
                    <p className="text-sm text-[#7a7a84] mt-1">
                        Manage homepage brand partners — upload logos or paste a URL. Recommended logo size: 400×200 px (transparent PNG/SVG), max 8 MB.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={syncFromCatalog} className="btn btn-white btn-sm" data-testid="admin-brands-sync">
                        <RefreshCw size={14} /> Sync from catalog
                    </button>
                    <button onClick={() => { setForm(emptyBrand); setEdit(null); setOpen(true); }} className="btn btn-primary btn-sm" data-testid="admin-brands-add">
                        <Plus size={14} /> Add brand
                    </button>
                </div>
            </div>
            <div className="bike-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#fafafa]">
                        <tr className="text-left uppercase text-xs text-[#7a7a84]">
                            <th className="p-3">Logo</th>
                            <th className="p-3">Brand</th>
                            <th className="p-3">Models</th>
                            <th className="p-3">Order</th>
                            <th className="p-3">Status</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(b => (
                            <tr key={b.id} className="border-t border-[#e5e7eb]">
                                <td className="p-3">
                                    <div className="w-16 h-10 bg-[#F4F6F8] rounded border border-zinc-200 flex items-center justify-center overflow-hidden">
                                        {b.logo_url ? (
                                            <img src={resolveAsset(b.logo_url)} alt="" className="max-h-8 max-w-[56px] object-contain" />
                                        ) : (
                                            <span className="text-xs text-zinc-400">—</span>
                                        )}
                                    </div>
                                </td>
                                <td className="p-3 font-medium">{b.name}</td>
                                <td className="p-3">{b.model_count ?? 0}</td>
                                <td className="p-3">{b.sort_order}</td>
                                <td className="p-3">
                                    <span className={`chip text-[10px] ${b.is_active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                                        {b.is_active ? "Live" : "Hidden"}
                                    </span>
                                </td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    <button onClick={() => { setForm(b); setEdit(b); setOpen(true); }} className="p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#fff5f5] hover:text-[#ED1C24] rounded"><Pencil size={14} /></button>
                                    <button onClick={() => remove(b)} className="p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#fff5f5] hover:text-[#ED1C24] rounded"><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="p-8 text-center text-[#7a7a84]">No brands yet. Click “Sync from catalog” to import from your bike inventory.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit brand" : "Add brand"} wide>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Brand name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                    <TextField label="Sort order" type="number" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) || 0 })} />
                    <div className="md:col-span-2">
                        <TextField label="Logo URL" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} />
                        <ImageSizeHint>
                            Use upload below, or paste <code className="text-[10px]">/api/uploads/…</code> or <code className="text-[10px]">/assets/brands/honda.svg</code>
                        </ImageSizeHint>
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">Upload logo</label>
                        <ImageSizeHint>
                            Recommended: 400×200 px (or square 400×400) · transparent PNG/SVG · max 8 MB. Shown ~120 px wide on the homepage.
                        </ImageSizeHint>
                        <div className="flex items-center gap-3 flex-wrap mt-2">
                            <button type="button" onClick={() => fileRef.current?.click()} className="btn btn-white btn-sm" disabled={uploading}>
                                {uploading ? "Uploading…" : "Choose file"}
                            </button>
                            {form.logo_url && (
                                <div className="h-12 px-3 bg-[#F4F6F8] rounded border border-zinc-200 flex items-center">
                                    <img src={resolveAsset(form.logo_url)} alt="" className="max-h-10 max-w-[120px] object-contain" />
                                </div>
                            )}
                        </div>
                        <input ref={fileRef} type="file" accept="image/*,.svg" className="hidden" onChange={(e) => onLogoUpload(e.target.files)} />
                    </div>
                    <label className="md:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                        Show on homepage
                    </label>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={() => setOpen(false)} className="btn btn-white btn-sm">Cancel</button>
                    <button onClick={save} className="btn btn-primary btn-sm">Save</button>
                </div>
            </Modal>
        </div>
    );
};

/* ===== Leads ===== */
const INTENT_LABELS = {
    general: "Callback",
    find_outlet: "Find outlet",
    test_ride: "Test ride",
    pre_book: "Pre-book",
    price_drop: "Price alert",
    whatsapp_inquiry: "On-road quote",
    loan_inquiry: "EMI / loan",
    compare_help: "Compare help",
    buy: "Buy",
};

const leadPhoneDigits = (phone) => (phone || "").replace(/\D/g, "").replace(/^0/, "").slice(-10);

const timeAgo = (iso) => {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const Leads = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [intentFilter, setIntentFilter] = useState("all");
    const [datePreset, setDatePreset] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [selected, setSelected] = useState(null);
    const [adminNote, setAdminNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/leads")
            .then((r) => setItems(r.data || []))
            .catch(() => toast.error("Failed to load leads"))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (selected) setAdminNote(selected.admin_note || "");
    }, [selected]);

    const stats = useMemo(() => {
        const today = new Date().toDateString();
        return {
            total: items.length,
            new: items.filter((l) => (l.status || "new") === "new").length,
            contacted: items.filter((l) => l.status === "contacted").length,
            closed: items.filter((l) => l.status === "closed").length,
            today: items.filter((l) => new Date(l.created_at).toDateString() === today).length,
        };
    }, [items]);

    const phoneCounts = useMemo(() => {
        const m = {};
        items.forEach((l) => {
            const p = leadPhoneDigits(l.phone);
            if (p) m[p] = (m[p] || 0) + 1;
        });
        return m;
    }, [items]);

    const intents = useMemo(() => [...new Set(items.map((l) => l.intent).filter(Boolean))].sort(), [items]);

    const applyDatePreset = (preset) => {
        setDatePreset(preset);
        if (preset === "all") {
            setDateFrom("");
            setDateTo("");
            return;
        }
        if (preset === "custom") return;
        const { from, to } = datePresetRange(preset);
        setDateFrom(from);
        setDateTo(to);
    };

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return items.filter((l) => {
            if (statusFilter !== "all" && (l.status || "new") !== statusFilter) return false;
            if (intentFilter !== "all" && l.intent !== intentFilter) return false;
            if (!leadInDateRange(l, dateFrom, dateTo)) return false;
            if (!term) return true;
            const hay = [l.name, l.phone, l.email, l.city, l.bike_name, l.purpose, l.intent, l.note, l.admin_note]
                .filter(Boolean).join(" ").toLowerCase();
            return hay.includes(term);
        });
    }, [items, q, statusFilter, intentFilter, dateFrom, dateTo]);

    const setStatus = async (id, status) => {
        try {
            const { data } = await api.put(`/admin/leads/${id}`, { status });
            setItems((prev) => prev.map((l) => (l.id === id ? { ...l, ...data } : l)));
            if (selected?.id === id) setSelected((s) => ({ ...s, ...data }));
            toast.success(`Marked as ${status}`);
        } catch {
            toast.error("Failed to update status");
        }
    };

    const saveAdminNote = async () => {
        if (!selected) return;
        setSavingNote(true);
        try {
            const { data } = await api.put(`/admin/leads/${selected.id}`, { admin_note: adminNote });
            setItems((prev) => prev.map((l) => (l.id === selected.id ? { ...l, ...data } : l)));
            setSelected((s) => ({ ...s, ...data }));
            toast.success("Note saved");
        } catch {
            toast.error("Failed to save note");
        } finally {
            setSavingNote(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this lead?")) return;
        await api.delete(`/admin/leads/${id}`);
        if (selected?.id === id) setSelected(null);
        toast.success("Deleted");
        load();
    };

    const statusClass = (s) => {
        if (s === "contacted") return "bg-amber-100 text-amber-800";
        if (s === "closed") return "bg-emerald-100 text-emerald-800";
        return "bg-[#fff5f5] text-[#ED1C24]";
    };

    const leadWaLink = (l) => {
        const digits = leadPhoneDigits(l.phone);
        const msg = encodeURIComponent(`Hi ${l.name}, this is Servall Bikes regarding your ${purposeLabel(l.purpose) || l.intent} enquiry.`);
        return digits ? `https://wa.me/91${digits}?text=${msg}` : "#";
    };

    const exportCsv = () => {
        if (!filtered.length) {
            toast.error("No leads to export for this filter.");
            return;
        }
        const header = ["Date", "Name", "Phone", "Email", "City", "Purpose", "Bike", "Intent", "Note", "Admin note", "Status", "Location"];
        const rows = filtered.map((l) => [
            new Date(l.created_at).toLocaleString("en-IN"),
            l.name, l.phone, l.email, l.city, l.purpose || "", l.bike_name, l.intent, l.note || "",
            l.admin_note || "", l.status || "new",
            l.location ? `${l.location.lat},${l.location.lng}` : "",
        ]);
        const csv = [header, ...rows].map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `servall-leads-${dateFrom || "all"}${dateTo ? `_to_${dateTo}` : ""}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportPdf = () => {
        if (!filtered.length) {
            toast.error("No leads to export for this filter.");
            return;
        }
        try {
            exportLeadsPdf(filtered, {
                dateFrom,
                dateTo,
                purposeLabel,
                intentLabels: INTENT_LABELS,
            });
            toast.success(`PDF exported (${filtered.length} leads)`);
        } catch {
            toast.error("PDF export failed");
        }
    };

    return (
        <div data-testid="admin-leads-page">
            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl">Leads &amp; Inquiries</h1>
                    <p className="text-sm text-[#7a7a84] mt-1">Filter, follow up, and track every sales enquiry.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={load} className="btn btn-white btn-sm" disabled={loading}>
                        <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                    <button type="button" onClick={exportPdf} className="btn btn-primary btn-sm" data-testid="leads-export-pdf">
                        <FileDown size={14} /> Export PDF
                    </button>
                    <button type="button" onClick={exportCsv} className="btn btn-white btn-sm" data-testid="leads-export">
                        <Download size={14} /> CSV
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 mb-5">
                {[
                    { label: "Total", value: stats.total, onClick: () => { setStatusFilter("all"); applyDatePreset("all"); } },
                    { label: "New", value: stats.new, onClick: () => setStatusFilter("new"), accent: stats.new > 0 },
                    { label: "Contacted", value: stats.contacted, onClick: () => setStatusFilter("contacted") },
                    { label: "Closed", value: stats.closed, onClick: () => setStatusFilter("closed") },
                    { label: "Today", value: stats.today, onClick: () => { setStatusFilter("all"); applyDatePreset("today"); }, accent: stats.today > 0 },
                ].map((s) => (
                    <button
                        key={s.label}
                        type="button"
                        onClick={s.onClick}
                        className={`bike-card p-4 text-left transition ${s.accent ? "ring-2 ring-[#ED1C24]/30" : ""}`}
                    >
                        <p className="text-[10px] uppercase font-bold text-[#7a7a84]">{s.label}</p>
                        <p className="font-display font-black text-2xl mt-1">{s.value}</p>
                    </button>
                ))}
            </div>

            <div className="bike-card p-4 mb-5 space-y-3">
                <div className="flex flex-col lg:flex-row gap-3">
                    <div className="input-with-icon flex-1">
                        <Search size={16} className="text-[#7a7a84]" aria-hidden />
                        <input
                            className="input w-full"
                            placeholder="Search name, phone, bike, city…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="leads-search"
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {["all", "new", "contacted", "closed"].map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setStatusFilter(s)}
                                className={`px-3 py-2 min-h-[44px] rounded-full text-xs font-bold uppercase ${statusFilter === s ? "bg-[#ED1C24] text-white" : "bg-[#fafafa] text-[#7a7a84] hover:bg-[#fff5f5]"}`}
                            >
                                {s}
                            </button>
                        ))}
                        <select className="input py-2 text-sm w-full sm:w-auto sm:min-w-[140px]" value={intentFilter} onChange={(e) => setIntentFilter(e.target.value)}>
                            <option value="all">All intents</option>
                            {intents.map((i) => selectOption(i, INTENT_LABELS[i] || i))}
                        </select>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-end gap-3 pt-2 border-t border-zinc-100">
                    <div className="flex items-center gap-2 text-xs font-bold text-[#7a7a84] uppercase shrink-0">
                        <Calendar size={14} /> Date
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {[
                            ["all", "All"],
                            ["today", "Today"],
                            ["yesterday", "Yesterday"],
                            ["week", "Last 7 days"],
                            ["month", "Last 30 days"],
                            ["custom", "Custom"],
                        ].map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => applyDatePreset(key)}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${datePreset === key ? "bg-[#0A2540] text-white" : "bg-[#fafafa] text-[#7a7a84] hover:bg-zinc-100"}`}
                                data-testid={`leads-date-${key}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    {(datePreset === "custom" || dateFrom || dateTo) && (
                        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                            <label className="text-xs text-[#7a7a84]">
                                From
                                <input
                                    type="date"
                                    className="input py-1.5 text-sm ml-1"
                                    value={dateFrom}
                                    onChange={(e) => { setDateFrom(e.target.value); setDatePreset("custom"); }}
                                    data-testid="leads-date-from"
                                />
                            </label>
                            <label className="text-xs text-[#7a7a84]">
                                To
                                <input
                                    type="date"
                                    className="input py-1.5 text-sm ml-1"
                                    value={dateTo}
                                    onChange={(e) => { setDateTo(e.target.value); setDatePreset("custom"); }}
                                    data-testid="leads-date-to"
                                />
                            </label>
                            {(dateFrom || dateTo) && (
                                <button type="button" onClick={() => applyDatePreset("all")} className="text-xs text-[#ED1C24] font-semibold hover:underline">
                                    Clear dates
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <p className="text-[11px] text-[#7a7a84]">
                    Showing <strong>{filtered.length}</strong> of {items.length} leads
                    {(dateFrom || dateTo) && ` · ${dateFrom || "…"} to ${dateTo || "…"}`}
                </p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
                <div className="xl:col-span-3 bike-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#fafafa]">
                                <tr className="text-left uppercase text-[10px] text-[#7a7a84]">
                                    <th className="p-3">When</th>
                                    <th className="p-3">Lead</th>
                                    <th className="p-3">Purpose</th>
                                    <th className="p-3">Source</th>
                                    <th className="p-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((l) => {
                                    const repeat = phoneCounts[leadPhoneDigits(l.phone)] > 1;
                                    const active = selected?.id === l.id;
                                    return (
                                        <tr
                                            key={l.id}
                                            onClick={() => setSelected(l)}
                                            className={`border-t border-[#e5e7eb] cursor-pointer transition ${active ? "bg-[#fff5f5]" : "hover:bg-[#fafafa]"}`}
                                            data-testid={`lead-row-${l.id}`}
                                        >
                                            <td className="p-3 whitespace-nowrap">
                                                <p className="font-medium text-xs">{timeAgo(l.created_at)}</p>
                                                <p className="text-[10px] text-[#7a7a84]">{new Date(l.created_at).toLocaleDateString("en-IN")}</p>
                                            </td>
                                            <td className="p-3">
                                                <p className="font-semibold">{l.name}</p>
                                                <p className="text-xs text-[#ED1C24]">{l.phone}</p>
                                                {repeat && <span className="text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold">REPEAT</span>}
                                            </td>
                                            <td className="p-3 text-xs">
                                                <p>{purposeLabel(l.purpose) || "—"}</p>
                                                {l.bike_name && <p className="text-[#7a7a84] truncate max-w-[120px]">{l.bike_name}</p>}
                                            </td>
                                            <td className="p-3">
                                                <span className="chip chip-red text-[9px]">{INTENT_LABELS[l.intent] || l.intent}</span>
                                            </td>
                                            <td className="p-3">
                                                <span className={`text-[10px] font-bold rounded-full px-2 py-1 ${statusClass(l.status || "new")}`}>
                                                    {l.status || "new"}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {!loading && filtered.length === 0 && (
                        <p className="p-10 text-center text-[#7a7a84]">{items.length ? "No leads match filters." : "No leads yet."}</p>
                    )}
                    {loading && <p className="p-10 text-center text-[#7a7a84]">Loading…</p>}
                </div>

                <div className="xl:col-span-2">
                    {selected ? (
                        <div className="bike-card p-5 sticky top-4" data-testid="lead-detail-panel">
                            <div className="flex items-start justify-between gap-2 mb-4">
                                <div>
                                    <h2 className="font-display font-bold text-xl">{selected.name}</h2>
                                    <p className="text-xs text-[#7a7a84] flex items-center gap-1 mt-1">
                                        <Clock size={12} /> {new Date(selected.created_at).toLocaleString("en-IN")}
                                    </p>
                                </div>
                                <button type="button" onClick={() => remove(selected.id)} className="p-2 hover:bg-[#fff5f5] text-[#ED1C24] rounded">
                                    <Trash2 size={16} />
                                </button>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-4">
                                {["new", "contacted", "closed"].map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setStatus(selected.id, s)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize ${(selected.status || "new") === s ? statusClass(s) : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}
                                        data-testid={`lead-status-${selected.id}-${s}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>

                            <dl className="space-y-3 text-sm mb-4">
                                <div className="flex gap-3">
                                    <dt className="text-[#7a7a84] w-20 shrink-0">Phone</dt>
                                    <dd><a href={`tel:${leadPhoneDigits(selected.phone)}`} className="text-[#ED1C24] font-semibold">{selected.phone}</a></dd>
                                </div>
                                {selected.email && (
                                    <div className="flex gap-3">
                                        <dt className="text-[#7a7a84] w-20 shrink-0">Email</dt>
                                        <dd><a href={`mailto:${selected.email}`} className="hover:underline">{selected.email}</a></dd>
                                    </div>
                                )}
                                {selected.city && (
                                    <div className="flex gap-3">
                                        <dt className="text-[#7a7a84] w-20 shrink-0">City</dt>
                                        <dd>{selected.city}</dd>
                                    </div>
                                )}
                                <div className="flex gap-3">
                                    <dt className="text-[#7a7a84] w-20 shrink-0">Purpose</dt>
                                    <dd>{purposeLabel(selected.purpose) || "—"}</dd>
                                </div>
                                <div className="flex gap-3">
                                    <dt className="text-[#7a7a84] w-20 shrink-0">Intent</dt>
                                    <dd>{INTENT_LABELS[selected.intent] || selected.intent}</dd>
                                </div>
                                {selected.bike_name && (
                                    <div className="flex gap-3">
                                        <dt className="text-[#7a7a84] w-20 shrink-0">Bike</dt>
                                        <dd>
                                            {selected.bike_id ? (
                                                <Link to={`/bikes/${selected.bike_id}`} target="_blank" className="text-[#ED1C24] hover:underline inline-flex items-center gap-1">
                                                    {selected.bike_name} <ExternalLink size={12} />
                                                </Link>
                                            ) : selected.bike_name}
                                        </dd>
                                    </div>
                                )}
                                {selected.note && (
                                    <div>
                                        <dt className="text-[#7a7a84] text-xs mb-1">Customer note</dt>
                                        <dd className="text-xs bg-[#fafafa] rounded-lg p-3 border border-zinc-100">{selected.note}</dd>
                                    </div>
                                )}
                                {selected.location?.lat != null && (
                                    <div className="flex gap-3">
                                        <dt className="text-[#7a7a84] w-20 shrink-0">GPS</dt>
                                        <dd>
                                            <a
                                                href={`https://www.google.com/maps?q=${selected.location.lat},${selected.location.lng}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[#ED1C24] text-xs inline-flex items-center gap-1 hover:underline"
                                            >
                                                <MapPin size={12} /> View on map
                                            </a>
                                        </dd>
                                    </div>
                                )}
                            </dl>

                            <div className="flex flex-wrap gap-2 mb-5">
                                <a href={`tel:${leadPhoneDigits(selected.phone)}`} className="btn btn-primary btn-sm flex-1 justify-center">
                                    <Phone size={14} /> Call
                                </a>
                                <a href={leadWaLink(selected)} target="_blank" rel="noopener noreferrer" className="btn btn-sm flex-1 justify-center bg-[#25D366] text-white hover:bg-[#1ea952]">
                                    <MessageCircle size={14} /> WhatsApp
                                </a>
                            </div>

                            <div>
                                <label className="label">Internal note</label>
                                <textarea
                                    className="input text-sm"
                                    rows={3}
                                    value={adminNote}
                                    onChange={(e) => setAdminNote(e.target.value)}
                                    placeholder="Follow-up notes, quote given, visit scheduled…"
                                    data-testid="lead-admin-note"
                                />
                                <button type="button" onClick={saveAdminNote} disabled={savingNote} className="btn btn-white btn-sm mt-2">
                                    {savingNote ? "Saving…" : "Save note"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="bike-card p-8 text-center text-[#7a7a84]">
                            <Filter size={28} className="mx-auto mb-2 opacity-30" />
                            <p className="text-sm">Select a lead to view details and follow up.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ===== Site Settings (contact, branding — hero copy lives in Homepage CMS) ===== */
const SettingsPage = () => {
    const { reload } = useSettings();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadAdminSettings = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/settings");
            setForm({ ...data, smtp_password: "" });
        } catch (e) {
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAdminSettings(); }, []);

    if (loading || !form) return <p className="text-[#7a7a84]">Loading…</p>;

    const save = async () => {
        try {
            const payload = { ...form };
            // Don't send empty password — backend keeps the existing one
            if (!payload.smtp_password) delete payload.smtp_password;
            delete payload.smtp_password_set;
            const { data } = await api.put("/admin/settings", payload);
            setForm({ ...data, smtp_password: "" });
            await reload();
            toast.success("Settings saved");
        } catch (e) { toast.error("Failed"); }
    };

    return (
        <div data-testid="admin-settings-page">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl">Site Settings</h1>
                    <p className="text-sm text-[#7a7a84] mt-1">
                        Contact details, email SMTP, global SEO, and tracking codes. Per-page titles are under{" "}
                        <Link to="/auth/secure-gate/pages" className="text-[#ED1C24] font-semibold hover:underline">Page Content</Link>.
                        Homepage hero copy is in{" "}
                        <Link to="/auth/secure-gate/cms" className="text-[#ED1C24] font-semibold hover:underline">Homepage CMS</Link>.
                    </p>
                </div>
                <button onClick={save} className="btn btn-primary btn-sm" data-testid="settings-save">Save Changes</button>
            </div>

            <div className="bike-card p-6 mb-5">
                <h3 className="font-display font-bold text-lg mb-4">Branding</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Site Name" value={form.site_name} onChange={(v) => setForm({ ...form, site_name: v })} />
                    <TextField label="Tagline" value={form.site_tagline} onChange={(v) => setForm({ ...form, site_tagline: v })} />
                </div>
            </div>

            <div className="bike-card p-6 mb-5">
                <h3 className="font-display font-bold text-lg mb-4">Contact & Social</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Support Email" value={form.support_email} onChange={(v) => setForm({ ...form, support_email: v })} />
                    <TextField label="Support Phone" value={form.support_phone} onChange={(v) => setForm({ ...form, support_phone: v })} />
                    <TextField label="WhatsApp Number (with +91)" value={form.social_whatsapp} onChange={(v) => setForm({ ...form, social_whatsapp: v })} />
                    <TextField label="Instagram URL" value={form.social_instagram} onChange={(v) => setForm({ ...form, social_instagram: v })} />
                    <div className="md:col-span-2"><TextField label="YouTube URL" value={form.social_youtube} onChange={(v) => setForm({ ...form, social_youtube: v })} /></div>
                </div>
            </div>

            <div className="bike-card p-6 mb-5" data-testid="admin-email-notify">
                <h3 className="font-display font-bold text-lg mb-1">Email alerts &amp; SMTP</h3>
                <p className="text-xs text-[#7a7a84] mb-4">
                    Configure SMTP here (saved in the database). When a customer submits Contact or a Lead, we email the notify address below.
                    Leave password blank when saving to keep the current password.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <TextField
                        label="SMTP host"
                        value={form.smtp_host ?? ""}
                        onChange={(v) => setForm({ ...form, smtp_host: v })}
                        placeholder="smtp.gmail.com"
                    />
                    <TextField
                        label="SMTP port"
                        value={form.smtp_port ?? "587"}
                        onChange={(v) => setForm({ ...form, smtp_port: v })}
                        placeholder="587"
                    />
                    <TextField
                        label="SMTP username"
                        value={form.smtp_user ?? ""}
                        onChange={(v) => setForm({ ...form, smtp_user: v })}
                        placeholder="you@gmail.com"
                    />
                    <TextField
                        label={form.smtp_password_set ? "SMTP password (set — leave blank to keep)" : "SMTP password"}
                        type="password"
                        value={form.smtp_password ?? ""}
                        onChange={(v) => setForm({ ...form, smtp_password: v })}
                        placeholder={form.smtp_password_set ? "••••••••" : ""}
                        autoComplete="new-password"
                    />
                    <div className="md:col-span-2">
                        <TextField
                            label="From address"
                            value={form.smtp_from ?? ""}
                            onChange={(v) => setForm({ ...form, smtp_from: v })}
                            placeholder="noreply@yourdomain.com"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.smtp_tls !== false}
                            onChange={(e) => setForm({ ...form, smtp_tls: e.target.checked })}
                        />
                        Use STARTTLS (port 587)
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!form.smtp_ssl}
                            onChange={(e) => setForm({ ...form, smtp_ssl: e.target.checked })}
                        />
                        Use SSL (port 465)
                    </label>
                </div>
                <div className="border-t border-zinc-200 pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                        <TextField
                            label="Notify email (inbox for alerts)"
                            value={form.notify_email ?? ""}
                            onChange={(v) => setForm({ ...form, notify_email: v })}
                            placeholder="sales@yourdomain.com"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.email_notify_contact !== false}
                            onChange={(e) => setForm({ ...form, email_notify_contact: e.target.checked })}
                        />
                        Email me on Contact form submissions
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.email_notify_leads !== false}
                            onChange={(e) => setForm({ ...form, email_notify_leads: e.target.checked })}
                        />
                        Email me on new Leads
                    </label>
                </div>
            </div>

            <div className="bike-card p-6 mb-5">
                <TextField label="Footer Text" value={form.footer_text} onChange={(v) => setForm({ ...form, footer_text: v })} />
            </div>

            <div className="bike-card p-6 mb-5" data-testid="admin-seo-settings">
                <h3 className="font-display font-bold text-lg mb-1">SEO &amp; Analytics</h3>
                <p className="text-xs text-[#7a7a84] mb-4">
                    Global defaults, tracking IDs, and social preview image. Per-page titles live under{" "}
                    <Link to="/auth/secure-gate/pages" className="text-[#ED1C24] font-semibold hover:underline">Page Content → On-page SEO</Link>.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                        <TextField label="Default browser title (fallback)" value={form.seo_default_title ?? ""} onChange={(v) => setForm({ ...form, seo_default_title: v })} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">Default meta description</label>
                        <textarea className="input" rows={2} value={form.seo_default_description ?? ""} onChange={(e) => setForm({ ...form, seo_default_description: e.target.value })} />
                    </div>
                    <div className="md:col-span-2">
                        <TextField label="Meta keywords (comma-separated)" value={form.seo_keywords ?? ""} onChange={(v) => setForm({ ...form, seo_keywords: v })} />
                    </div>
                    <TextField label="Canonical base URL" value={form.seo_canonical_base ?? ""} onChange={(v) => setForm({ ...form, seo_canonical_base: v })} />
                    <div>
                        <TextField label="Social preview image URL (og:image)" value={form.seo_og_image ?? ""} onChange={(v) => setForm({ ...form, seo_og_image: v })} />
                        <ImageSizeHint>Recommended: 1200×630 px (1.91:1) · JPG/PNG · under 1 MB for faster sharing</ImageSizeHint>
                    </div>
                </div>
                {form.seo_og_image ? (
                    <div className="mt-3 w-full max-w-xs aspect-[1200/630] bg-[#F4F6F8] rounded-lg overflow-hidden border border-zinc-200">
                        <img src={resolveAsset(form.seo_og_image)} alt="OG preview" className="w-full h-full object-cover" />
                    </div>
                ) : null}
            </div>

            <div className="bike-card p-6 mb-5">
                <h3 className="font-display font-bold text-lg mb-4">Tracking codes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Google Analytics 4 ID (G-…)" value={form.google_analytics_id ?? ""} onChange={(v) => setForm({ ...form, google_analytics_id: v })} />
                    <TextField label="Google Ads ID (AW-…)" value={form.google_ads_id ?? ""} onChange={(v) => setForm({ ...form, google_ads_id: v })} />
                    <TextField
                        label="Google Ads conversion label (AW-…/…)"
                        value={form.google_ads_conversion_label ?? ""}
                        onChange={(v) => setForm({ ...form, google_ads_conversion_label: v })}
                    />
                    <p className="md:col-span-2 text-[11px] text-[#7a7a84] -mt-1">
                        For lead/conversion tracking, paste the full conversion ID from Google Ads → Goals → Conversions (format: AW-123456789/AbCdEfGhIj). Leave blank to skip conversion events.
                    </p>
                    <TextField label="Google Search Console verification" value={form.google_site_verification ?? ""} onChange={(v) => setForm({ ...form, google_site_verification: v })} />
                    <TextField label="Meta (Facebook) Pixel ID" value={form.meta_pixel_id ?? ""} onChange={(v) => setForm({ ...form, meta_pixel_id: v })} />
                </div>
                <div className="mt-4 space-y-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.tracking_enabled !== false} onChange={(e) => setForm({ ...form, tracking_enabled: e.target.checked })} />
                        Enable tracking scripts
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.tracking_production_only !== false} onChange={(e) => setForm({ ...form, tracking_production_only: e.target.checked })} />
                        Load tracking only on servallbikes.in (skip localhost / preview)
                    </label>
                </div>
            </div>
        </div>
    );
};

/* ===== Hero Showcase Images (4-image auto-loop) ===== */
const HeroShowcaseEditor = () => {
    const [images, setImages] = useState([]);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        api.get("/config/showcase-images").then(r => setImages(r.data?.images || []));
    }, []);

    const save = async (next) => {
        setImages(next);
        try {
            await api.put("/admin/showcase-images", { images: next });
            toast.success("Hero showcase updated / live on homepage");
        } catch { toast.error("Failed to save"); }
    };

    const onFiles = async (files) => {
        if (!files || files.length === 0) return;
        setBusy(true);
        const next = [...images];
        try {
            for (const f of Array.from(files)) {
                if (next.length >= 8) {
                    toast.error("Max 8 images");
                    break;
                }
                const { url } = await uploadFile(f);
                next.push(url);
            }
            await save(next);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const remove = (i) => save(images.filter((_, idx) => idx !== i));
    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= images.length) return;
        const next = [...images];
        [next[i], next[j]] = [next[j], next[i]];
        save(next);
    };

    return (
        <div className="bike-card p-6 mb-5 border-2 border-[#E31837]/20" data-testid="hero-showcase-editor">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <h3 className="font-display font-bold text-lg">Hero Showcase / Auto-Loop Carousel</h3>
                    <p className="text-xs text-[#7a7a84] mt-1">
                        Upload 4 bike photos — they cycle every 3 seconds on the homepage hero. Drag-drop or click to pick.
                    </p>
                    <p className="text-[11px] text-[#0A2540]/80 mt-1.5 font-medium">
                        Recommended size: 1920×1080 px (16:9) · Min 1280×720 · JPG/PNG/WEBP · max 8 MB each
                    </p>
                </div>
                <span className="text-xs px-3 py-1 rounded-full bg-[#E31837]/10 text-[#E31837] font-black">{images.length} / 8</span>
            </div>

            {/* Drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2", "ring-[#E31837]"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-[#E31837]")}
                onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("ring-2", "ring-[#E31837]");
                    onFiles(e.dataTransfer.files);
                }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-zinc-300 rounded-xl p-6 text-center cursor-pointer hover:bg-zinc-50 transition-colors"
                data-testid="hero-dropzone"
            >
                <p className="text-sm font-bold text-[#0A2540]">
                    {busy ? "Uploading…" : "📷 Drag-drop images here or click to upload"}
                </p>
                <p className="text-[11px] text-[#7a7a84] mt-1">1920×1080 px · PNG / JPG / WEBP · max 8 MB each</p>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="hero-file-input" />
            </div>

            {/* Image grid */}
            {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                    {images.map((url, i) => (
                        <div key={`${url}-${i}`} className="relative group aspect-[4/3] bg-[#F4F6F8] rounded-lg overflow-hidden border border-zinc-200" data-testid={`showcase-img-${i}`}>
                            <img src={resolveAsset(url)} alt="" className="w-full h-full object-contain p-2" />
                            <span className="absolute top-1 left-1 text-[9px] font-black bg-black/70 text-white px-1.5 py-0.5 rounded">{i + 1}</span>
                            <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => move(i, -1)} disabled={i === 0} className="bg-white text-[#0A2540] w-7 h-7 rounded-full text-xs font-bold disabled:opacity-30" title="Move left">←</button>
                                <button onClick={() => move(i, 1)} disabled={i === images.length - 1} className="bg-white text-[#0A2540] w-7 h-7 rounded-full text-xs font-bold disabled:opacity-30" title="Move right">→</button>
                                <button onClick={() => remove(i)} className="bg-[#E31837] text-white w-7 h-7 rounded-full flex items-center justify-center" title="Remove" data-testid={`showcase-remove-${i}`}>
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ===== Homepage quick filters editor ===== */
const QuickFiltersEditor = ({ filters = [], onChange }) => {
    const list = Array.isArray(filters) ? filters : [];

    const patch = (i, key, val) => {
        const next = list.map((row, j) => (j === i ? { ...row, [key]: val } : row));
        onChange(next);
    };

    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= list.length) return;
        const next = [...list];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };

    const add = () =>
        onChange([
            ...list,
            { id: `filter-${Date.now()}`, label: "New filter", category: "", fuel_type: "", brand: "", price_min: "", price_max: "", sort: "", featured_only: false },
        ]);

    const remove = (i) => onChange(list.filter((_, j) => j !== i));

    return (
        <div className="space-y-3" data-testid="admin-quick-filters">
            {list.map((f, i) => (
                <div key={f.id || i} className="border border-zinc-200 rounded-xl p-4 bg-[#fafafa]">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <p className="text-xs font-bold text-[#0A2540]">Filter {i + 1}</p>
                        <div className="flex gap-1">
                            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="btn btn-ghost btn-xs">↑</button>
                            <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} className="btn btn-ghost btn-xs">↓</button>
                            <button type="button" onClick={() => remove(i)} className="btn btn-ghost btn-xs text-[#ED1C24]"><Trash2 size={12} /></button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <TextField label="Chip label" value={f.label ?? ""} onChange={(v) => patch(i, "label", v)} />
                        <div>
                            <label className="label">Category</label>
                            <select className="input" value={f.category ?? ""} onChange={(e) => patch(i, "category", e.target.value)}>
                                <option value="">Any</option>
                                <option value="Scooty & EV">Scooty &amp; EV</option>
                                <option value="Motorcycle">Motorcycle</option>
                            </select>
                        </div>
                        <div>
                            <label className="label">Fuel</label>
                            <select className="input" value={f.fuel_type ?? ""} onChange={(e) => patch(i, "fuel_type", e.target.value)}>
                                <option value="">Any</option>
                                <option value="Petrol">Petrol</option>
                                <option value="Electric">Electric</option>
                            </select>
                        </div>
                        <TextField label="Brand" value={f.brand ?? ""} onChange={(v) => patch(i, "brand", v)} />
                        <TextField label="Price min (₹)" value={f.price_min ?? ""} onChange={(v) => patch(i, "price_min", v)} />
                        <TextField label="Price max (₹)" value={f.price_max ?? ""} onChange={(v) => patch(i, "price_max", v)} />
                        <div>
                            <label className="label">Sort</label>
                            <select className="input" value={f.sort ?? ""} onChange={(e) => patch(i, "sort", e.target.value)}>
                                <option value="">Default</option>
                                <option value="popularity">Popularity</option>
                                <option value="price_asc">Price low → high</option>
                                <option value="price_desc">Price high → low</option>
                                <option value="rating">Top rated</option>
                                <option value="newest">Newest</option>
                            </select>
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer sm:col-span-2 lg:col-span-3">
                            <input type="checkbox" checked={Boolean(f.featured_only)} onChange={(e) => patch(i, "featured_only", e.target.checked)} />
                            Featured bikes only
                        </label>
                    </div>
                </div>
            ))}
            <button type="button" onClick={add} className="btn btn-secondary btn-sm"><Plus size={14} /> Add quick filter</button>
        </div>
    );
};

/* ===== Page Content CMS (all public pages) ===== */
const PageContentCMS = () => {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => { api.get("/config/pages").then(r => setCfg(r.data?.pages || {})); }, []);

    const save = async () => {
        if (!cfg) return;
        setSaving(true);
        try {
            const { data } = await api.put("/admin/pages-config", cfg);
            setCfg(data);
            toast.success("Page content updated — live on all customer pages");
        } catch { toast.error("Failed to save"); }
        finally { setSaving(false); }
    };

    if (!cfg) return <p className="text-[#7a7a84]">Loading…</p>;

    const seoPages = [
        { key: "home", label: "Homepage", path: "/" },
        { key: "bikes", label: "Bikes catalogue", path: "/bikes" },
        { key: "compare", label: "Compare", path: "/compare" },
        { key: "emi", label: "EMI calculator", path: "/emi" },
        { key: "dealers", label: "Outlets / dealers", path: "/dealers" },
        { key: "contact", label: "Contact", path: "/contact" },
        { key: "news", label: "News listing", path: "/news" },
        { key: "about", label: "About us", path: "/about" },
        { key: "privacy", label: "Privacy policy", path: "/privacy-policy" },
    ];

    const groups = [
        { title: "Bikes catalogue", fields: [["bikes_eyebrow", "Eyebrow"], ["bikes_title", "Title"], ["bikes_subtitle", "Subtitle", true]] },
        { title: "Compare", fields: [["compare_eyebrow", "Eyebrow"], ["compare_title", "Title"], ["compare_subtitle", "Subtitle", true]] },
        { title: "EMI calculator", fields: [
            ["emi_eyebrow", "Eyebrow"], ["emi_title", "Title"], ["emi_subtitle", "Subtitle", true],
            ["emi_down_pct", "Down payment %"], ["emi_default_rate", "Default interest %"], ["emi_tenures", "Tenure options (comma-separated)"],
        ]},
        { title: "Outlets / dealers", fields: [
            ["dealers_eyebrow", "Eyebrow"], ["dealers_title", "Title"], ["dealers_subtitle", "Subtitle", true],
            ["dealers_thanks", "Form thank-you. Use {name} for the visitor's first name.", true],
        ]},
        { title: "Contact", fields: [
            ["contact_eyebrow", "Eyebrow"], ["contact_title", "Title"], ["contact_subtitle", "Subtitle", true],
            ["contact_office_label", "Office label"], ["contact_office_address", "Office address", true],
        ]},
        { title: "News", fields: [
            ["news_eyebrow", "Eyebrow"], ["news_title", "Title"], ["news_subtitle", "Subtitle", true], ["news_cta_label", "CTA label"],
            ["news_detail_cta_title", "Article CTA title"], ["news_detail_cta_sub", "Article CTA subtitle", true],
        ]},
        { title: "Home — bike finder", fields: [
            ["home_finder_eyebrow", "Eyebrow"], ["home_finder_title", "Title"], ["home_finder_subtitle", "Subtitle", true],
        ]},
        { title: "Home — brands & how-to", fields: [
            ["home_brands_eyebrow", "Brands eyebrow"], ["home_brands_title", "Brands title"], ["home_brands_sub", "Brands subtitle", true],
            ["home_how_eyebrow", "How-to eyebrow"], ["home_how_title", "How-to title"],
            ["home_nearest_title", "Nearest outlet title"], ["home_nearest_sub", "Nearest outlet subtitle", true],
        ]},
        { title: "Home — trust block", fields: [
            ["trust_eyebrow", "Eyebrow"],
            ["trust_tab1_label", "Tab 1 label"], ["trust_tab1_body", "Tab 1 body", true],
            ["trust_tab2_label", "Tab 2 label"], ["trust_tab2_body", "Tab 2 body", true],
            ["trust_tab3_label", "Tab 3 label"], ["trust_tab3_body", "Tab 3 body", true],
            ["trust_stat_riders_value", "Riders stat number"], ["trust_stat_riders_label", "Riders stat label"],
        ]},
        { title: "Home — pricing & satisfaction", fields: [
            ["home_guarantee_eyebrow", "Eyebrow"], ["home_guarantee_title", "Title"], ["home_guarantee_sub", "Subtitle", true],
            ["home_pricing_title", "Pricing card title"], ["home_pricing_body", "Pricing card body", true],
            ["home_satisfaction_title", "Satisfaction card title"], ["home_satisfaction_body", "Satisfaction card body", true],
        ]},
        { title: "Privacy policy", fields: [["privacy_last_updated", "Last updated"]] },
        { title: "About us", fields: [
            ["about_eyebrow", "Eyebrow"], ["about_title", "Title"], ["about_subtitle", "Subtitle", true],
            ["about_sec1_title", "Section 1 title"], ["about_sec1_body", "Section 1 body", true],
            ["about_sec2_title", "Section 2 title"], ["about_sec2_body", "Section 2 body", true],
            ["about_sec3_title", "Section 3 title"], ["about_sec3_body", "Section 3 body", true],
            ["about_sec4_title", "Section 4 title"], ["about_sec4_body", "Section 4 body", true],
        ]},
    ];

    return (
        <div data-testid="admin-pages-cms">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl">Page Content</h1>
                    <p className="text-sm text-[#7a7a84] mt-1">Page copy, on-page SEO titles, EMI defaults, and trust block for every customer-facing route.</p>
                </div>
                <button onClick={save} disabled={saving} className="btn btn-primary btn-sm">{saving ? "Saving…" : "Save page content"}</button>
            </div>
            {groups.map((g) => (
                <div key={g.title} className="bike-card p-6 mb-5">
                    <h3 className="font-display font-bold text-lg mb-4">{g.title}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {g.fields.map(([key, label, wide]) => (
                            <div key={key} className={wide ? "md:col-span-2" : ""}>
                                {wide ? (
                                    <>
                                        <label className="label">{label}</label>
                                        <textarea className="input" rows={2} value={cfg[key] ?? ""} onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} />
                                    </>
                                ) : (
                                    <TextField label={label} value={cfg[key] ?? ""} onChange={(v) => setCfg({ ...cfg, [key]: v })} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <div className="bike-card p-6 mb-5">
                <h3 className="font-display font-bold text-lg mb-1">Home — quick filters</h3>
                <p className="text-xs text-[#7a7a84] mb-4">
                    One-tap chips on the homepage bike finder. Reorder with ↑↓. When smart brands is on, top inventory brands are added automatically (without duplicating brands you set here).
                </p>
                <QuickFiltersEditor
                    filters={cfg.home_quick_filters}
                    onChange={(home_quick_filters) => setCfg({ ...cfg, home_quick_filters })}
                />
                <div className="mt-4 space-y-2 border-t border-zinc-100 pt-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={cfg.home_smart_brand_filters !== false}
                            onChange={(e) => setCfg({ ...cfg, home_smart_brand_filters: e.target.checked })}
                        />
                        Auto-add top 4 brands from live inventory as smart chips
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={cfg.home_show_upcoming !== false}
                            onChange={(e) => setCfg({ ...cfg, home_show_upcoming: e.target.checked })}
                        />
                        Show upcoming launches strip on homepage
                    </label>
                </div>
            </div>
            <div className="bike-card p-6 mb-5" data-testid="admin-page-seo">
                <h3 className="font-display font-bold text-lg mb-1">On-page SEO</h3>
                <p className="text-xs text-[#7a7a84] mb-4">Browser tab title and meta description for each route. Global defaults and tracking IDs are under Settings.</p>
                {seoPages.map(({ key, label, path }) => (
                    <div key={key} className="border-t border-zinc-100 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
                        <p className="text-sm font-bold text-[#0A2540] mb-2">{label} <span className="text-[#7a7a84] font-normal">{path}</span></p>
                        <div className="grid grid-cols-1 gap-3">
                            <TextField
                                label="SEO title"
                                value={cfg[`${key}_seo_title`] ?? ""}
                                onChange={(v) => setCfg({ ...cfg, [`${key}_seo_title`]: v })}
                            />
                            <div>
                                <label className="label">Meta description</label>
                                <textarea
                                    className="input"
                                    rows={2}
                                    value={cfg[`${key}_seo_description`] ?? ""}
                                    onChange={(e) => setCfg({ ...cfg, [`${key}_seo_description`]: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                ))}
                <div className="border-t border-zinc-200 pt-5 mt-5">
                    <p className="text-sm font-bold text-[#0A2540] mb-2">Bike detail page <span className="text-[#7a7a84] font-normal">/bikes/:id</span></p>
                    <p className="text-[11px] text-[#7a7a84] mb-2">Use {"{name}"} and {"{brand}"} placeholders.</p>
                    <div className="grid grid-cols-1 gap-3">
                        <TextField label="Title template" value={cfg.bike_detail_seo_title_template ?? ""} onChange={(v) => setCfg({ ...cfg, bike_detail_seo_title_template: v })} />
                        <div>
                            <label className="label">Description template</label>
                            <textarea className="input" rows={2} value={cfg.bike_detail_seo_description_template ?? ""} onChange={(e) => setCfg({ ...cfg, bike_detail_seo_description_template: e.target.value })} />
                        </div>
                    </div>
                </div>
                <div className="border-t border-zinc-200 pt-5 mt-5">
                    <p className="text-sm font-bold text-[#0A2540] mb-2">News article page <span className="text-[#7a7a84] font-normal">/news/:slug</span></p>
                    <p className="text-[11px] text-[#7a7a84] mb-2">Use {"{title}"} and {"{excerpt}"} placeholders.</p>
                    <div className="grid grid-cols-1 gap-3">
                        <TextField label="Title template" value={cfg.news_detail_seo_title_template ?? ""} onChange={(v) => setCfg({ ...cfg, news_detail_seo_title_template: v })} />
                        <div>
                            <label className="label">Description template</label>
                            <textarea className="input" rows={2} value={cfg.news_detail_seo_description_template ?? ""} onChange={(e) => setCfg({ ...cfg, news_detail_seo_description_template: e.target.value })} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="bike-card p-6 mb-5">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={cfg.trust_use_live_stats !== false}
                        onChange={(e) => setCfg({ ...cfg, trust_use_live_stats: e.target.checked })}
                    />
                    Use live outlet &amp; rider counts on homepage trust block (from database)
                </label>
            </div>
        </div>
    );
};

/* ===== Site CMS (homepage_config) ===== */
const SiteCMS = () => {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => { api.get("/config/homepage-layout").then(r => setCfg(r.data)); }, []);

    const save = async () => {
        if (!cfg) return;
        setSaving(true);
        try {
            const { data } = await api.put("/admin/homepage-config", cfg);
            setCfg(data);
            toast.success("Homepage CMS updated / live across all visitors");
        } catch { toast.error("Failed to save"); }
        finally { setSaving(false); }
    };

    if (!cfg) return <p className="text-[#7a7a84]">Loading config…</p>;

    const groups = [
        { title: "Topbar", fields: [
            ["topbarAlert", "Topbar Alert Text"],
        ]},
        { title: "Hero Section", fields: [
            ["heroEyebrow", "Eyebrow Tagline"],
            ["heroHeadlineLine1", "Headline Line 1"],
            ["heroHeadlineLine2", "Headline Line 2"],
            ["heroHeadlineAccent", "Headline Accent (crimson)"],
            ["heroSubcopy", "Hero Sub-copy", true],
            ["ctaPrimaryLabel", "Primary CTA Label"],
            ["ctaSecondaryLabel", "Secondary CTA Label"],
        ]},
        { title: "Trust Metrics Block", fields: [
            ["metricsTitle", "Metrics Headline", true],
            ["metricsSub", "Metrics Sub-copy", true],
        ]},
        { title: "Footer CTA Block", fields: [
            ["ctaFooterTitle", "Footer CTA Title", true],
            ["ctaFooterSub", "Footer CTA Sub-copy", true],
            ["ctaFooterButton", "Footer Button Label"],
        ]},
    ];

    return (
        <div data-testid="admin-cms-page">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl">Site CMS / Homepage</h1>
                    <p className="text-sm text-[#7a7a84] mt-1">
                        This is the only place to edit homepage hero text, carousel images, and footer CTA. Contact phone/email are under Settings.
                    </p>
                </div>
                <button onClick={save} disabled={saving} className="btn btn-primary btn-sm" data-testid="cms-save">
                    {saving ? "Saving…" : "Save Global UI Layout Updates"}
                </button>
            </div>

            <HeroShowcaseEditor />

            {groups.map((g) => (
                <div key={g.title} className="bike-card p-6 mb-5">
                    <h3 className="font-display font-bold text-lg mb-4">{g.title}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {g.fields.map(([key, label, wide]) => (
                            <div key={key} className={wide ? "md:col-span-2" : ""}>
                                {wide ? (
                                    <>
                                        <label className="label">{label}</label>
                                        <textarea
                                            className="input"
                                            rows={2}
                                            value={cfg[key] || ""}
                                            onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
                                            data-testid={`cms-field-${key}`}
                                        />
                                    </>
                                ) : (
                                    <TextField
                                        label={label}
                                        value={cfg[key] || ""}
                                        onChange={(v) => setCfg({ ...cfg, [key]: v })}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};


/* ===== Contact Messages Module ===== */
const ManageMessages = () => {
    const [data, setData] = useState({ items: [], unread_count: 0, total: 0 });
    const [filter, setFilter] = useState("all"); // all | unread | read
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter !== "all") params.status = filter;
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/admin/messages", { params });
            setData(data);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const markRead = async (m) => {
        const status = m.status === "read" ? "unread" : "read";
        await api.put(`/admin/messages/${m.id}`, { status });
        toast.success(`Marked ${status}`);
        if (selected?.id === m.id) setSelected({ ...selected, status });
        await load();
    };

    const remove = async (m) => {
        if (!window.confirm(`Delete message from ${m.name}?`)) return;
        await api.delete(`/admin/messages/${m.id}`);
        toast.success("Deleted");
        if (selected?.id === m.id) setSelected(null);
        await load();
    };

    const formatDate = (iso) => {
        try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
        catch { return iso; }
    };

    return (
        <div data-testid="admin-messages-page">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-3 flex-wrap">
                        Contact Messages
                        {data.unread_count > 0 && <span className="bg-[#E31837] text-white text-xs font-black px-2.5 py-1 rounded-full">{data.unread_count} new</span>}
                    </h1>
                    <p className="text-sm text-[#7a7a84] mt-1">Submissions from the public Contact Us page. Total: {data.total}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-4 items-center">
                {[
                    { k: "all", label: "All" },
                    { k: "unread", label: "Unread" },
                    { k: "read", label: "Read" },
                ].map((f) => (
                    <button
                        key={f.k}
                        onClick={() => setFilter(f.k)}
                        className={`px-3 py-1.5 min-h-[44px] rounded-full text-xs font-black uppercase tracking-wider transition ${filter === f.k ? "bg-[#E31837] text-white" : "bg-zinc-100 text-[#0A2540] hover:bg-zinc-200"}`}
                        data-testid={`msg-filter-${f.k}`}
                    >
                        {f.label}
                    </button>
                ))}
                <div className="input-with-icon w-full sm:w-auto sm:ml-auto">
                    <Search size={14} className="text-zinc-400" aria-hidden />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search name, email, subject…"
                        className="input w-full sm:w-64"
                        data-testid="msg-search"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* List */}
                <div className="lg:col-span-5 bike-card p-0 overflow-hidden max-h-[70vh] overflow-y-auto">
                    {loading ? (
                        <p className="p-6 text-zinc-500">Loading…</p>
                    ) : data.items.length === 0 ? (
                        <p className="p-6 text-zinc-500">No messages found.</p>
                    ) : (
                        data.items.map((m) => (
                            <button
                                key={m.id}
                                onClick={() => { setSelected(m); if (m.status === "unread") markRead(m); }}
                                className={`w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition flex items-start gap-3 ${selected?.id === m.id ? "bg-[#F4F6F8]" : ""}`}
                                data-testid={`msg-row-${m.id}`}
                            >
                                <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${m.status === "unread" ? "bg-[#E31837]" : "bg-zinc-300"}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className={`font-bold text-sm text-[#0A2540] truncate ${m.status === "unread" ? "" : "opacity-70"}`}>{m.name}</p>
                                        <p className="text-[10px] text-zinc-400 flex-shrink-0">{formatDate(m.created_at).split(",")[0]}</p>
                                    </div>
                                    <p className="text-xs text-zinc-500 truncate">{m.email}</p>
                                    <p className="text-xs text-[#0A2540] truncate mt-1 font-semibold">{m.subject}</p>
                                </div>
                            </button>
                        ))
                    )}
                </div>

                {/* Detail */}
                <div className="lg:col-span-7">
                    {selected ? (
                        <div className="bike-card p-6 space-y-4" data-testid="msg-detail">
                            <div className="flex items-start justify-between flex-wrap gap-3">
                                <div>
                                    <h2 className="font-display font-bold text-xl text-[#0A2540]">{selected.subject}</h2>
                                    <p className="text-xs text-zinc-500 mt-1">From <span className="font-bold text-[#0A2540]">{selected.name}</span> &lt;{selected.email}&gt;</p>
                                    <p className="text-xs text-zinc-400 mt-0.5">{formatDate(selected.created_at)}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => markRead(selected)} className="btn btn-white btn-sm" data-testid="msg-toggle-read">
                                        <Eye size={14} /> Mark {selected.status === "read" ? "Unread" : "Read"}
                                    </button>
                                    <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`} className="btn btn-primary btn-sm" data-testid="msg-reply">
                                        <Mail size={14} /> Reply
                                    </a>
                                    <button onClick={() => remove(selected)} className="btn btn-sm bg-red-50 text-[#E31837] border border-red-200" data-testid="msg-delete">
                                        <Trash2 size={14} /> Delete
                                    </button>
                                </div>
                            </div>
                            <div className="border-t border-zinc-100 pt-4">
                                <p className="text-sm text-[#0A2540] leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                            </div>
                            <p className="text-[11px] text-zinc-400 pt-2 border-t border-zinc-100">
                                Consent: {selected.consent ? <span className="text-emerald-600 font-bold">Yes</span> : <span className="text-zinc-500">Not given</span>}
                            </p>
                        </div>
                    ) : (
                        <div className="bike-card p-10 text-center text-zinc-400">
                            <Mail size={32} className="mx-auto mb-2 opacity-40" />
                            <p>Select a message to view details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ===== Main wrapper with role guard ===== */
const AdminPanel = () => {
    const { user, loading } = useAuth();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [menuOpen]);

    if (loading) return <div className="p-10">Loading…</div>;
    if (!user) return <AdminLogin />;
    if (user.role !== "admin") return (
        <div className="container-x py-20 text-center" data-testid="admin-denied">
            <h2 className="font-display font-bold text-2xl sm:text-3xl">Access Denied</h2>
            <p className="text-[#4a4a52] mt-2">You need admin privileges to view this page.</p>
            <Link to="/" className="btn btn-primary mt-5">Back to Home</Link>
        </div>
    );

    return (
        <div className="flex h-screen overflow-hidden bg-[#fafafa]" data-testid="admin-panel">
            <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)} />
            <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
                <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-[#e5e7eb] flex items-center gap-1 px-2 h-14">
                    <button
                        type="button"
                        onClick={() => setMenuOpen(true)}
                        className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg hover:bg-[#fafafa]"
                        aria-label="Open admin menu"
                        data-testid="admin-menu-toggle"
                    >
                        <Menu size={22} />
                    </button>
                    <p className="font-display font-bold text-sm truncate">{adminMobileTitle(location.pathname)}</p>
                </header>
                <main className="flex-1 min-h-0 p-4 md:p-10 overflow-y-auto overflow-x-hidden">
                    <Routes>
                        <Route index element={<Dashboard />} />
                        <Route path="bikes" element={<ManageBikes />} />
                        <Route path="news" element={<ManageNews />} />
                        <Route path="dealers" element={<ManageDealers />} />
                        <Route path="brands" element={<ManageBrands />} />
                        <Route path="messages" element={<ManageMessages />} />
                        <Route path="cms" element={<SiteCMS />} />
                        <Route path="pages" element={<PageContentCMS />} />
                        <Route path="leads" element={<Leads />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="*" element={<Navigate to="/auth/secure-gate" replace />} />
                    </Routes>
                </main>
            </div>
        </div>
    );
};

export default AdminPanel;
